import { useState } from 'react'
import { KeyRound, Lock } from 'lucide-react'
import { Alert } from './ui/alert.tsx'
import { Button } from './ui/button.tsx'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog.tsx'
import { Input, Label } from './ui/input.tsx'
import { api, ApiError } from '../lib/api.ts'

/**
 * Lišta zamčeného seznamu. Neutrální pás, ne destructive: zamčený seznam je
 * typicky hotový seznam a hotový seznam má vypadat hotově.
 */
export function LockBanner({ listId, onUnlocked }: { listId: string; onUnlocked: () => void }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Alert className="no-print" id="lock-reason">
        <Lock size={16} aria-hidden className="shrink-0 text-[var(--muted-foreground)]" />
        <p>
          Seznam je <strong>uzamčený</strong>. Prohlížení, tisk i export jsou volné — pro úpravy výběru je potřeba heslo.
        </p>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
          <KeyRound size={14} aria-hidden /> Odemknout úpravy
        </Button>
      </Alert>
      <UnlockDialog listId={listId} open={open} onOpenChange={setOpen} onUnlocked={onUnlocked} />
    </>
  )
}

/**
 * Dialog na heslo seznamu. Sdílený — kromě zamčeného editoru ho používá i mazání
 * seznamu v přehledu, ať se heslo nikde nezadává dvěma různými způsoby.
 */
export function UnlockDialog({
  listId, open, onOpenChange, onUnlocked, description,
}: {
  listId: string
  open: boolean
  onOpenChange: (v: boolean) => void
  onUnlocked: () => void
  description?: string
}) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [remaining, setRemaining] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.unlock(listId, password)
      setPassword('')
      onOpenChange(false)
      onUnlocked()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { remainingAttempts?: number } | undefined) : undefined
      setRemaining(body?.remainingAttempts ?? null)
      setError(err instanceof Error ? err.message : 'Odemčení se nepodařilo')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Odemknout úpravy</DialogTitle>
        <DialogDescription>
          {description ?? 'Čtení, tisk i export fungují i bez hesla — heslo je potřeba jen na změnu výběru.'}
        </DialogDescription>

        <form onSubmit={submit} className="mt-4 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="unlock-password">Heslo seznamu</Label>
            <Input
              id="unlock-password"
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'unlock-error' : undefined}
            />
          </div>

          {error && (
            <p id="unlock-error" role="alert" className="text-[length:var(--text-sm)] text-[var(--destructive)]">
              {error}
              {remaining !== null && remaining > 0 && ` Zbývají ${remaining} pokusy.`}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Zrušit</Button>
            <Button type="submit" disabled={busy || password.length === 0}>
              {busy ? 'Ověřuji…' : 'Odemknout'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
